<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary btn-sm" @click="goBackToList">
          <i class="ri-arrow-left-line"></i> 返回
        </button>
        <button type="button" class="btn btn-secondary" @click="handleReset">重置</button>
        <button
          v-if="activeTab !== 'records'"
          type="button"
          class="btn btn-primary"
          :disabled="submitting"
          @click="handleSubmit"
        >
          <span v-if="submitting"><i class="ri-loader-4-line spin"></i> 儲存中...</span>
          <span v-else><i class="ri-save-line"></i> 儲存設定</span>
        </button>
      </div>
    </div>

    <div class="page-container">
    <div v-if="isWixtarClient" class="wixtar-gift-notice">
      <i class="ri-information-line"></i>
      <div>
        <p class="wixtar-gift-notice-title">Wixtar 會員模式：註冊禮分工</p>
        <ul>
          <li>請至
            <router-link to="/client/settings?tab=wixtar">系統參數 → Wixtar 整合</router-link>
            開啟「會員事件觸發註冊禮」後，由 Wixtar 會員新建（webhook／LIFF 寫入 Wixtar 後的 CRM 首次建檔）統一發禮。
          </li>
          <li>Wixtar 管道對應 Wixtar 會員新建（webhook）；LINE 管道對應 LIFF 新註冊。LIFF 本機不會再另發一次。</li>
          <li>若 Wixtar 端已有註冊禮，或要用行銷活動做複雜註冊流程，請關閉該開關並停用本頁活動，避免重複。</li>
        </ul>
      </div>
    </div>

    <!-- Tab 導航 -->
    <div class="tab-nav">
      <button
        class="tab-btn"
        :class="{ active: activeTab === 'basic' }"
        @click="activeTab = 'basic'"
      >
        <i class="ri-settings-3-line"></i> 基本設定
      </button>
      <button
        class="tab-btn"
        :class="{ active: activeTab === 'rewards' }"
        @click="activeTab = 'rewards'"
      >
        <i class="ri-coupon-line"></i> 點數 &amp; 優惠券
      </button>
      <button
        class="tab-btn"
        :class="{ active: activeTab === 'records' }"
        @click="activeTab = 'records'"
      >
        <i class="ri-list-check"></i> 活動紀錄
      </button>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <template v-else>
      <!-- ======== Tab 1：基本設定 ======== -->
      <div v-show="activeTab === 'basic'" class="card">
        <div class="card-body">
          <form @submit.prevent="handleSubmit">
            <!-- 基本設定 -->
            <div class="form-section">
              <h2 class="section-title">基本設定</h2>
              <div class="form-group">
                <label class="form-label">活動名稱 <span class="required">*</span></label>
                <input
                  v-model="form.moduleName"
                  type="text"
                  class="form-control"
                  maxlength="100"
                  placeholder="例如：2026 新會員註冊禮"
                />
                <span v-if="errors.moduleName" class="error-text">{{ errors.moduleName }}</span>
              </div>
              <div class="form-row-grid">
                <div class="form-group">
                  <label class="form-label">生效開始時間</label>
                  <input v-model="form.activeStartAtLocal" type="datetime-local" class="form-control" />
                </div>
                <div class="form-group">
                  <label class="form-label">生效結束時間</label>
                  <input v-model="form.activeEndAtLocal" type="datetime-local" class="form-control" />
                  <small class="form-text">留空表示無結束時間</small>
                </div>
                <div class="form-group">
                  <label class="form-label">排序（數字小優先）</label>
                  <input v-model.number="form.sortOrder" type="number" class="form-control" />
                </div>
              </div>
              <div class="form-group">
                <div class="toggle-row">
                  <label class="toggle-label">啟用此註冊禮活動</label>
                  <label class="toggle-switch">
                    <input v-model="form.isEnabled" type="checkbox" />
                    <span class="toggle-slider"></span>
                  </label>
                  <span class="toggle-status" :class="form.isEnabled ? 'on' : 'off'">
                    {{ form.isEnabled ? '已啟用' : '已停用' }}
                  </span>
                </div>
              </div>
            </div>

            <!-- 通知設定 -->
            <div class="form-section">
              <h2 class="section-title">通知設定</h2>
              <NotificationChannelSelector
                v-model:channels="form.notificationChannels"
                v-model:templateIds="form.notificationTemplateIds"
                show-default-template-preview
              />
              <span v-if="errors.notificationChannels" class="error-text" style="display:block;margin-top:8px;">
                {{ errors.notificationChannels }}
              </span>
            </div>

          </form>
        </div>
      </div>

      <!-- ======== Tab 2：點數 & 優惠券 ======== -->
      <div v-show="activeTab === 'rewards'" class="card">
        <div class="card-body">
          <form @submit.prevent="handleSubmit">
            <!-- 依註冊管道設定 -->
            <div class="form-section">
              <h2 class="section-title">依註冊管道設定</h2>
              <p class="section-description">為不同的註冊管道設定不同的註冊禮內容</p>

              <div v-for="(channelConfig, index) in form.channelConfigs" :key="index" class="channel-config-card">
                <div class="channel-config-header">
                  <div class="channel-config-title">
                    <i class="ri-settings-3-line"></i>
                    <span>{{ getChannelName(channelConfig.registrationChannel) }}</span>
                  </div>
                  <button
                    type="button"
                    class="btn btn-sm btn-danger"
                    @click="removeChannelConfig(index)"
                    :disabled="form.channelConfigs.length <= 1"
                  >
                    <i class="ri-delete-bin-line"></i> 刪除
                  </button>
                </div>

                <div class="channel-config-body">
                  <div class="form-group">
                    <label class="form-label">註冊管道 <span class="required">*</span></label>
                    <select
                      v-model="channelConfig.registrationChannel"
                      class="form-control"
                      :class="{ 'is-invalid': errors[`channelConfigs.${index}.registrationChannel`] }"
                    >
                      <option value="line">LINE（LIFF 註冊）</option>
                      <option value="phone">手機／商城</option>
                      <option value="pos">{{ isWixtarClient ? 'Wixtar' : 'POS' }}</option>
                      <option value="batch_import">批次匯入</option>
                    </select>
                    <span v-if="errors[`channelConfigs.${index}.registrationChannel`]" class="error-text">
                      {{ errors[`channelConfigs.${index}.registrationChannel`] }}
                    </span>
                    <small v-if="isWixtarClient" class="form-text">
                      Wixtar 模式：需先開啟「會員事件觸發註冊禮」。Wixtar＝webhook 會員新建；LINE＝LIFF 新註冊。
                    </small>
                  </div>

                  <!-- 點數設定 -->
                  <div class="form-group">
                    <div class="toggle-row">
                      <label class="toggle-label">發送點數</label>
                      <label class="toggle-switch">
                        <input v-model="channelConfig.sendPoints" type="checkbox" />
                        <span class="toggle-slider"></span>
                      </label>
                      <span class="toggle-status" :class="channelConfig.sendPoints ? 'on' : 'off'">
                        {{ channelConfig.sendPoints ? '已啟用' : '已停用' }}
                      </span>
                    </div>
                  </div>
                  <div v-if="channelConfig.sendPoints" class="form-group">
                    <label class="form-label">點數數量 <span class="required">*</span></label>
                    <input
                      v-model.number="channelConfig.pointsAmount"
                      type="number"
                      class="form-control"
                      :class="{ 'is-invalid': errors[`channelConfigs.${index}.pointsAmount`] }"
                      placeholder="請輸入大於 0 的點數"
                      min="1"
                      @focus="$event.target.select()"
                    />
                    <div v-if="errors[`channelConfigs.${index}.pointsAmount`]" class="invalid-feedback">
                      {{ errors[`channelConfigs.${index}.pointsAmount`] }}
                    </div>
                  </div>
                  <div v-if="channelConfig.sendPoints" class="form-group">
                    <p class="form-text" style="margin-bottom: 0;">
                      贈點到期日依「客戶設定 → 點數過期設定」統一計算，與消費贈點相同。
                    </p>
                  </div>

                  <!-- 優惠券設定 -->
                  <div class="form-group">
                    <div class="toggle-row">
                      <label class="toggle-label">發送優惠券</label>
                      <label class="toggle-switch">
                        <input v-model="channelConfig.sendCoupons" type="checkbox" />
                        <span class="toggle-slider"></span>
                      </label>
                      <span class="toggle-status" :class="channelConfig.sendCoupons ? 'on' : 'off'">
                        {{ channelConfig.sendCoupons ? '已啟用' : '已停用' }}
                      </span>
                    </div>
                  </div>
                  <div v-if="channelConfig.sendCoupons" class="form-group">
                    <label class="form-label">優惠券模板 <span class="required">*</span></label>
                    <CouponTemplateMultiSelector
                      v-model="channelConfig.couponTemplateIds"
                      :templates="templates"
                      :loading="templatesLoading"
                      hint="可選擇多個優惠券模板，註冊禮會發送所有選中的優惠券"
                      @update:model-value="(ids) => syncSendCoupons(index, ids)"
                    />
                    <div v-if="errors[`channelConfigs.${index}.couponTemplateIds`]" class="invalid-feedback" style="display:block;">
                      {{ errors[`channelConfigs.${index}.couponTemplateIds`] }}
                    </div>
                  </div>

                  <div v-if="isWixtarClient" class="form-group">
                    <div class="toggle-row">
                      <label class="toggle-label">發送 Wixtar 票券</label>
                      <label class="toggle-switch">
                        <input v-model="channelConfig.sendWixtarTickets" type="checkbox" />
                        <span class="toggle-slider"></span>
                      </label>
                      <span class="toggle-status" :class="channelConfig.sendWixtarTickets ? 'on' : 'off'">
                        {{ channelConfig.sendWixtarTickets ? '已啟用' : '已停用' }}
                      </span>
                    </div>
                  </div>
                  <div v-if="isWixtarClient && channelConfig.sendWixtarTickets" class="form-group">
                    <label class="form-label">Wixtar 票券 <span class="required">*</span></label>
                    <WixtarTicketMultiSelect
                      :model-value="channelConfig.wixtarTicketIds || []"
                      :tickets="wixtarTickets"
                      :loading="wixtarTicketsLoading"
                      @update:model-value="(ids) => syncSendWixtarTickets(index, ids)"
                    />
                    <small class="form-text">可選擇多張 Wixtar 票券，註冊禮會各發送一張。會員需已綁定 Wixtar。</small>
                    <div v-if="errors[`channelConfigs.${index}.wixtarTicketIds`]" class="invalid-feedback" style="display:block;">
                      {{ errors[`channelConfigs.${index}.wixtarTicketIds`] }}
                    </div>
                  </div>
                </div>
              </div>

              <div class="form-group">
                <button type="button" class="btn btn-secondary" @click="addChannelConfig">
                  <i class="ri-add-line"></i> 新增註冊管道設定
                </button>
              </div>
            </div>

          </form>
        </div>
      </div>

      <!-- ======== Tab 3：活動紀錄 ======== -->
      <div v-show="activeTab === 'records'" class="card">
        <div class="card-body">
          <div class="page-toolbar">
            <div class="filter-group">
              <label class="filter-label">狀態</label>
              <select v-model="logsFilter.status" class="form-control">
                <option value="">全部狀態</option>
                <option value="success">成功</option>
                <option value="failed">失敗</option>
                <option value="pending">處理中</option>
                <option value="retrying">重試中</option>
              </select>
            </div>
            <div class="filter-group">
              <label class="filter-label">會員名稱</label>
              <input
                v-model="logsFilter.memberName"
                type="text"
                class="form-control"
                placeholder="搜尋會員名稱..."
                @keyup.enter="searchLogs"
              />
            </div>
            <div class="filter-group filter-group--action">
              <button type="button" class="btn btn-primary" @click="searchLogs">
                <i class="ri-search-line"></i> 查詢
              </button>
              <button type="button" class="btn btn-outline-secondary" @click="resetLogsFilter">
                <i class="ri-refresh-line"></i> 重置
              </button>
            </div>
          </div>

          <div v-if="!logsSearched" class="logs-empty-hint">
            <i class="ri-search-line"></i>
            <p>請輸入查詢條件後點擊「查詢」</p>
          </div>

          <template v-else>
            <div v-if="logsLoading" class="loading-state">
              <div class="spinner"></div>
              <p>載入活動紀錄...</p>
            </div>
            <div v-else class="table-wrapper">
              <table class="logs-table">
                <thead>
                  <tr>
                    <th>發送時間</th>
                    <th>會員</th>
                    <th>註冊管道</th>
                    <th>點數</th>
                    <th>優惠券</th>
                    <th v-if="isWixtarClient">Wixtar 票券</th>
                    <th>狀態</th>
                    <th>錯誤訊息</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="logs.length === 0">
                    <td :colspan="logsColspan" class="empty-state">尚無活動紀錄</td>
                  </tr>
                  <tr v-for="item in logs" :key="item.id">
                    <td>{{ formatLogDate(item.createdAt) }}</td>
                    <td>
                      <router-link
                        v-if="item.memberId"
                        :to="{ name: 'ClientMemberDetail', params: { id: item.memberId } }"
                        class="member-link"
                      >
                        {{ item.memberName || item.memberId }}
                      </router-link>
                      <span v-else>-</span>
                    </td>
                    <td>{{ getChannelName(item.registrationChannel) }}</td>
                    <td>{{ item.pointsSent || 0 }}</td>
                    <td>{{ item.couponsSent || 0 }}</td>
                    <td v-if="isWixtarClient">{{ item.wixtarTicketsSent || 0 }}</td>
                    <td>
                      <span :class="logStatusBadgeClass(item.status)">{{ logStatusLabel(item.status) }}</span>
                    </td>
                    <td class="error-cell">{{ item.errorMessage || '-' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <ListPagination
              v-if="logsPagination.totalPages > 1"
              :page="logsPagination.page"
              :total-pages="logsPagination.totalPages"
              :total="logsPagination.total"
              @change="goLogsPage"
            />
          </template>
        </div>
      </div>
    </template>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { autoSendService } from '@/api/modules/autoSend.js';
import { couponService } from '@/api/modules/coupons.js';
import { wixtarService } from '@/api/modules/wixtar.js';
import { clientService } from '@/api/modules/clients.js';
import authService from '@/helpers/authservice/authService.js';
import { showSuccess, showError } from '@/utils/swal.js';
import NotificationChannelSelector from '@/components/NotificationChannelSelector.vue';
import CouponTemplateMultiSelector from '@/components/CouponTemplateMultiSelector.vue';
import WixtarTicketMultiSelect from '@/components/WixtarTicketMultiSelect.vue';

const toDatetimeLocalValue = (isoString) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const fromDatetimeLocalValue = (localValue) => {
  if (!localValue) return null;
  const date = new Date(localValue);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

export default {
  name: 'RegistrationGiftConfigEdit',
  components: { NotificationChannelSelector, CouponTemplateMultiSelector, WixtarTicketMultiSelect },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { pageTitle } = usePageTitle('註冊禮設定');
    const loading = ref(false);
    const submitting = ref(false);
    const activeTab = ref('basic');
    const currentModuleId = ref(route.params.moduleId || null);

    const form = reactive({
      moduleName: '',
      activeStartAtLocal: '',
      activeEndAtLocal: '',
      sortOrder: 0,
      isEnabled: true,
      channelConfigs: [
        {
          registrationChannel: 'line',
          sendPoints: false,
          pointsAmount: 0,
          sendCoupons: false,
          couponTemplateIds: [],
          sendWixtarTickets: false,
          wixtarTicketIds: []
        }
      ],
      sendNotification: false,
      notificationChannel: null,
      customMessageTemplate: null,
      notificationChannels: [],
      notificationTemplateIds: {}
    });

    const errors = reactive({});
    const templates = ref([]);
    const templatesLoading = ref(false);
    const isWixtarClient = ref(false);
    const memberSystemLoaded = ref(false);
    const wixtarTickets = ref([]);
    const wixtarTicketsLoading = ref(false);
    const logsColspan = computed(() => (isWixtarClient.value ? 8 : 7));

    const emptyChannelConfig = () => ({
      registrationChannel: 'line',
      sendPoints: false,
      pointsAmount: 0,
      sendCoupons: false,
      couponTemplateIds: [],
      sendWixtarTickets: false,
      wixtarTicketIds: []
    });

    const parseIdList = (value) => {
      if (!value) return [];
      if (Array.isArray(value)) return value.map((id) => String(id));
      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value);
          return Array.isArray(parsed) ? parsed.map((id) => String(id)) : [String(parsed)];
        } catch {
          return [value];
        }
      }
      return [String(value)];
    };

    const logs = ref([]);
    const logsLoading = ref(false);
    const logsSearched = ref(false);
    const logsFilter = reactive({
      status: '',
      memberName: ''
    });
    const logsPagination = reactive({
      page: 1,
      pageSize: 20,
      total: 0,
      totalPages: 1
    });

    const goBackToList = () => {
      router.push({ name: 'RegistrationGiftCampaignList' });
    };

    // 取得註冊管道名稱
    const getChannelName = (channel) => {
      const channelMap = {
        'line': 'LINE',
        'phone': '手機／商城',
        'pos': isWixtarClient.value ? 'Wixtar' : 'POS',
        'batch_import': '批次匯入'
      };
      return channelMap[channel] || channel;
    };

    const syncSendCoupons = (index, ids) => {
      const channelConfig = form.channelConfigs[index];
      if (!channelConfig) return;
      channelConfig.sendCoupons = Array.isArray(ids) && ids.length > 0;
    };

    const syncSendWixtarTickets = (index, ids) => {
      const channelConfig = form.channelConfigs[index];
      if (!channelConfig) return;
      channelConfig.wixtarTicketIds = ids;
      channelConfig.sendWixtarTickets = Array.isArray(ids) && ids.length > 0;
    };

    const loadClientMemberSystem = async () => {
      const user = authService.getCurrentUser();
      if (!user?.clientId) {
        isWixtarClient.value = false;
        memberSystemLoaded.value = true;
        return;
      }
      try {
        const res = await clientService.getClient(user.clientId);
        isWixtarClient.value = res?.data?.clientData?.memberSystemType === 'WIXTAR';
        memberSystemLoaded.value = true;
      } catch (error) {
        console.error('載入會員模式失敗:', error);
        isWixtarClient.value = false;
        memberSystemLoaded.value = false;
      }
    };

    const loadWixtarTickets = async () => {
      if (!isWixtarClient.value) {
        wixtarTickets.value = [];
        return;
      }
      wixtarTicketsLoading.value = true;
      try {
        const all = [];
        let page = 1;
        let totalPages = 1;
        do {
          const response = await wixtarService.getTicketCatalog({
            page,
            pageSize: 100,
            sortBy: 'activity',
            sortOrder: 'asc'
          });
          all.push(...(response?.data?.items || []));
          totalPages = response?.data?.pagination?.totalPages || 1;
          page += 1;
        } while (page <= totalPages && page <= 10);
        wixtarTickets.value = all;
      } catch (error) {
        console.error('載入 Wixtar 票券失敗:', error);
        showError('載入 Wixtar 票券失敗', error.message);
      } finally {
        wixtarTicketsLoading.value = false;
      }
    };

    // 載入優惠券模板列表
    const loadTemplates = async () => {
      templatesLoading.value = true;
      try {
        const response = await couponService.getTemplates({
          pageSize: 100,
          isActive: true
        });
        if (response && response.success && response.data) {
          templates.value = response.data.items || [];
        }
      } catch (error) {
        console.error('載入優惠券模板失敗:', error);
        showError('載入優惠券模板失敗', error.message);
      } finally {
        templatesLoading.value = false;
      }
    };

    // 新增註冊管道設定
    const addChannelConfig = () => {
      form.channelConfigs.push(emptyChannelConfig());
    };

    // 移除註冊管道設定
    const removeChannelConfig = (index) => {
      if (form.channelConfigs.length > 1) {
        form.channelConfigs.splice(index, 1);
      }
    };

    const applyConfigToForm = (data) => {
      form.moduleName = data.moduleName || '';
      form.activeStartAtLocal = toDatetimeLocalValue(data.activeStartAt);
      form.activeEndAtLocal = toDatetimeLocalValue(data.activeEndAt);
      form.sortOrder = data.sortOrder ?? 0;
      form.isEnabled = data.isEnabled ?? false;
      form.channelConfigs = data.channelConfigs;
      form.sendNotification = data.sendNotification ?? false;
      form.notificationChannel = data.notificationChannel || null;
      form.customMessageTemplate = data.customMessageTemplate || null;
      form.notificationChannels = Array.isArray(data.notificationChannels) ? data.notificationChannels : [];
      form.notificationTemplateIds = data.notificationTemplateIds || {};
    };

    const normalizeChannelConfigs = () => {
      if (!form.channelConfigs || !Array.isArray(form.channelConfigs) || form.channelConfigs.length === 0) {
        form.channelConfigs = [emptyChannelConfig()];
      }

      form.channelConfigs.forEach((config) => {
        if (config.couponTemplateIds) {
          if (Array.isArray(config.couponTemplateIds)) {
            // ok
          } else if (typeof config.couponTemplateIds === 'string') {
            try {
              const parsed = JSON.parse(config.couponTemplateIds);
              config.couponTemplateIds = Array.isArray(parsed) ? parsed : [parsed];
            } catch {
              config.couponTemplateIds = [];
            }
          } else {
            config.couponTemplateIds = [config.couponTemplateIds];
          }
        } else {
          config.couponTemplateIds = [];
        }

        if (config.couponTemplateIds.length > 0) {
          config.sendCoupons = true;
        }

        config.wixtarTicketIds = parseIdList(config.wixtarTicketIds);
        if (config.wixtarTicketIds.length > 0) {
          config.sendWixtarTickets = true;
        } else if (config.sendWixtarTickets == null) {
          config.sendWixtarTickets = false;
        }
      });
    };

    // 載入設定
    const loadConfig = async (moduleId = currentModuleId.value) => {
      if (!moduleId) {
        showError('缺少活動 ID', '請從活動列表進入編輯頁');
        goBackToList();
        return;
      }

      loading.value = true;
      try {
        const response = await autoSendService.getRegistrationGiftModule(moduleId);

        if (response && response.success && response.data) {
          applyConfigToForm(response.data);
          normalizeChannelConfigs();
        }
      } catch (error) {
        console.error('載入設定失敗:', error);
        showError('載入設定失敗', error.message);
      } finally {
        loading.value = false;
      }
    };

    // 驗證表單
    const validateForm = () => {
      Object.keys(errors).forEach(key => delete errors[key]);

      if (!form.moduleName || !form.moduleName.trim()) {
        errors.moduleName = '請輸入活動名稱';
      }

      let isValid = !errors.moduleName;
      form.channelConfigs.forEach((config, index) => {
        if (!config.registrationChannel) {
          errors[`channelConfigs.${index}.registrationChannel`] = '請選擇註冊管道';
          isValid = false;
        }
        
        // 檢查是否有重複的註冊管道
        const duplicateIndex = form.channelConfigs.findIndex((c, i) => 
          i !== index && c.registrationChannel === config.registrationChannel
        );
        if (duplicateIndex !== -1) {
          errors[`channelConfigs.${index}.registrationChannel`] = '註冊管道不能重複';
          isValid = false;
        }

        // 驗證點數設定
        if (config.sendPoints) {
          if (!config.pointsAmount || config.pointsAmount <= 0) {
            errors[`channelConfigs.${index}.pointsAmount`] = '點數數量必須大於 0';
            isValid = false;
          }
        }

        // 驗證優惠券設定
        if (config.sendCoupons) {
          if (!config.couponTemplateIds || config.couponTemplateIds.length === 0) {
            errors[`channelConfigs.${index}.couponTemplateIds`] = '請至少選擇一個優惠券模板';
            isValid = false;
          }
        }

        if (isWixtarClient.value && config.sendWixtarTickets) {
          if (!config.wixtarTicketIds || config.wixtarTicketIds.length === 0) {
            errors[`channelConfigs.${index}.wixtarTicketIds`] = '請至少選擇一張 Wixtar 票券';
            isValid = false;
          }
        }
      });

      // 未選擇自訂範本時，發送端會使用系統預設範本
      errors.notificationChannels = '';

      return isValid;
    };

    // 提交表單
    const handleSubmit = async () => {
      if (!validateForm()) {
        const firstError = Object.values(errors).find(v => v);
        showError('驗證失敗', firstError || '請檢查輸入資料');
        return;
      }

      submitting.value = true;
      try {
        // 準備提交資料
        const channelConfigs = form.channelConfigs.map(config => ({
          registrationChannel: config.registrationChannel,
          sendPoints: config.sendPoints || false,
          pointsAmount: config.sendPoints ? (config.pointsAmount || 0) : null,
          pointsExpiryDays: null,
          sendCoupons: config.sendCoupons || false,
          couponTemplateIds: config.sendCoupons && config.couponTemplateIds && config.couponTemplateIds.length > 0
            ? config.couponTemplateIds
            : [],
          sendWixtarTickets: memberSystemLoaded.value && !isWixtarClient.value
            ? false
            : (config.sendWixtarTickets || false),
          wixtarTicketIds: memberSystemLoaded.value && !isWixtarClient.value
            ? []
            : (config.sendWixtarTickets && config.wixtarTicketIds && config.wixtarTicketIds.length > 0
              ? config.wixtarTicketIds
              : [])
        }));

        const data = {
          moduleName: form.moduleName.trim(),
          activeStartAt: fromDatetimeLocalValue(form.activeStartAtLocal) || new Date().toISOString(),
          activeEndAt: fromDatetimeLocalValue(form.activeEndAtLocal),
          sortOrder: form.sortOrder ?? 0,
          isEnabled: form.isEnabled,
          channelConfigs,
          sendNotification: form.notificationChannels && form.notificationChannels.length > 0,
          notificationChannel: form.notificationChannels && form.notificationChannels.length > 0
            ? form.notificationChannels[0] : null,
          customMessageTemplate: null,
          notificationChannels: form.notificationChannels || [],
          notificationTemplateIds: form.notificationTemplateIds || {}
        };

        const response = await autoSendService.updateRegistrationGiftModule(currentModuleId.value, data);

        if (response && response.success) {
          await showSuccess('設定儲存成功', '註冊禮設定已更新');
          await loadConfig(); // 重新載入以取得最新資料
        } else {
          showError('儲存設定失敗', response?.message || '未知錯誤');
        }
      } catch (error) {
        showError('儲存設定失敗', error.message || '請檢查輸入資料');
      } finally {
        submitting.value = false;
      }
    };

    // 重置表單
    const handleReset = () => {
      loadConfig();
    };

    const loadLogs = async () => {
      if (!currentModuleId.value) return;
      logsLoading.value = true;
      try {
        const params = {
          page: logsPagination.page,
          pageSize: logsPagination.pageSize,
          moduleId: currentModuleId.value
        };
        if (logsFilter.status) params.status = logsFilter.status;
        if (logsFilter.memberName?.trim()) params.memberName = logsFilter.memberName.trim();

        const response = await autoSendService.getRegistrationGiftLogs(params);
        if (response?.success && response.data) {
          logs.value = response.data.items || [];
          logsPagination.total = response.data.pagination?.totalItems || 0;
          logsPagination.totalPages = response.data.pagination?.totalPages || 1;
        }
      } catch (error) {
        showError('載入活動紀錄失敗', error.message);
      } finally {
        logsLoading.value = false;
      }
    };

    const searchLogs = () => {
      logsPagination.page = 1;
      logsSearched.value = true;
      loadLogs();
    };

    const resetLogsFilter = () => {
      logsFilter.status = '';
      logsFilter.memberName = '';
      logsPagination.page = 1;
      logsSearched.value = false;
      logs.value = [];
    };

    const goLogsPage = (page) => {
      logsPagination.page = page;
      loadLogs();
    };

    const formatLogDate = (value) => {
      if (!value) return '-';
      return new Date(value).toLocaleString('zh-TW');
    };

    const logStatusLabel = (status) => ({
      success: '成功',
      failed: '失敗',
      pending: '處理中',
      retrying: '重試中'
    }[status] || status || '-');

    const logStatusBadgeClass = (status) => ({
      success: 'badge badge-success',
      failed: 'badge badge-danger',
      pending: 'badge badge-warning',
      retrying: 'badge badge-secondary'
    }[status] || 'badge badge-secondary');

    watch(
      () => route.params.moduleId,
      async (moduleId) => {
        if (moduleId && moduleId !== currentModuleId.value) {
          currentModuleId.value = moduleId;
          activeTab.value = 'basic';
          resetLogsFilter();
          await loadConfig(moduleId);
        }
      }
    );

    onMounted(async () => {
      await loadClientMemberSystem();
      await Promise.all([loadConfig(), loadTemplates(), loadWixtarTickets()]);
    });

    return {
      pageTitle,
      loading,
      activeTab,
      form,
      errors,
      submitting,
      templates,
      templatesLoading,
      isWixtarClient,
      wixtarTickets,
      wixtarTicketsLoading,
      logsColspan,
      syncSendCoupons,
      syncSendWixtarTickets,
      logs,
      logsLoading,
      logsSearched,
      logsFilter,
      logsPagination,
      goBackToList,
      getChannelName,
      addChannelConfig,
      removeChannelConfig,
      handleSubmit,
      handleReset,
      searchLogs,
      resetLogsFilter,
      goLogsPage,
      formatLogDate,
      logStatusLabel,
      logStatusBadgeClass
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.wixtar-gift-notice {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  margin-bottom: 16px;
  padding: 12px 14px;
  background: #f0f7ff;
  border: 1px solid #cfe2ff;
  border-radius: 8px;
  color: #334155;
  font-size: 13px;
  line-height: 1.55;
}

.wixtar-gift-notice > i {
  color: #0d6efd;
  font-size: 18px;
  margin-top: 2px;
}

.wixtar-gift-notice-title {
  margin: 0 0 6px;
  font-weight: 600;
  color: #1e3a5f;
}

.wixtar-gift-notice ul {
  margin: 0;
  padding-left: 18px;
}

.wixtar-gift-notice li + li {
  margin-top: 4px;
}

/* ===== Toggle 開關 ===== */
.toggle-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.toggle-label {
  font-weight: 500;
  color: #333;
  font-size: 14px;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  flex-shrink: 0;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background: #ccc;
  border-radius: 24px;
  transition: background 0.2s;
}

.toggle-slider::before {
  content: '';
  position: absolute;
  width: 18px;
  height: 18px;
  left: 3px;
  top: 3px;
  background: white;
  border-radius: 50%;
  transition: transform 0.2s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
}

.toggle-switch input:checked + .toggle-slider {
  background: #4caf50;
}

.toggle-switch input:checked + .toggle-slider::before {
  transform: translateX(20px);
}

.toggle-status {
  font-size: 13px;
  font-weight: 500;
}

.toggle-status.on { color: #4caf50; }
.toggle-status.off { color: #9ca3af; }

/* ===== Tab 導航 ===== */
.tab-nav {
  display: flex;
  gap: 0;
  margin-bottom: 20px;
  border-bottom: 2px solid #e0e0e0;
}

.tab-btn {
  padding: 10px 24px;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  font-size: 14px;
  font-weight: 500;
  color: #666;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
}

.tab-btn:hover {
  color: #1976d2;
}

.tab-btn.active {
  color: #1976d2;
  border-bottom-color: #1976d2;
}

.form-section {
  margin-bottom: 30px;
}

.form-row-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
}

.filter-label {
  display: block;
  font-size: 12px;
  color: #6c757d;
  margin-bottom: 4px;
}

.logs-empty-hint {
  text-align: center;
  padding: 40px 20px;
  color: #6c757d;
}

.logs-empty-hint i {
  font-size: 32px;
  display: block;
  margin-bottom: 8px;
}

.logs-table {
  width: 100%;
  min-width: 900px;
}

.member-link {
  color: #2563eb;
  text-decoration: none;
}

.member-link:hover {
  text-decoration: underline;
}

.error-cell {
  max-width: 220px;
  word-break: break-word;
  color: #b91c1c;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 16px;
}

.page-info {
  font-size: 13px;
  color: #6c757d;
}

.badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
}

.badge-success { background: #d1fae5; color: #065f46; }
.badge-danger { background: #fee2e2; color: #991b1b; }
.badge-warning { background: #fef3c7; color: #92400e; }
.badge-secondary { background: #e5e7eb; color: #374151; }

.section-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e0e0e0;
}

.section-description {
  color: #666;
  font-size: 14px;
  margin-bottom: 20px;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
}

.required {
  color: #dc3545;
}

.error-text {
  display: block;
  color: #dc3545;
  font-size: 14px;
  margin-top: 5px;
}

.form-text {
  display: block;
  margin-top: 5px;
  color: #6c757d;
  font-size: 14px;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* 註冊管道設定卡片 */
.channel-config-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  margin-bottom: 20px;
  background: #fff;
}

.channel-config-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
  border-radius: 8px 8px 0 0;
}

.channel-config-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #333;
}

.channel-config-body {
  padding: 20px;
}

</style>
